import './index.css'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { pure, compose } from 'recompose'
import classNames from 'classnames'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'
import { selectors as UsersSelectors } from 'data/users'
import { getLabel } from 'data/users/record'
import DefaultUserAvatar from './default.svg'
import Contact from './contact.svg'
import ImageLoader from './imageLoader'

export const SIZES = {
  xxs: 12,
  xs: 18,
  small: 24,
  base: 32,
  large: 40,
}

const CLASSNAMES = {
  xxs: 'font-size-smaller',
  xs: 'font-size-smaller',
  small: 'font-size-normal',
  base: 'font-size-large',
  large: 'font-size-larger',
}

const STYLES = Object.keys(SIZES).reduce(
  (styles, size) => ({
    ...styles,
    [size]: {
      height: SIZES[size],
      width: SIZES[size],
    },
  }),
  {}
)

function getClassNamesForProps(props) {
  const {
    url,
    size,
    className,
    square = false,
    backgroundClass = 'background-shade-30',
    borderClass = props.noBorder ? '' : 'border-small border-shade-100',
    colorClass = 'shade-100',
    isWriting,
  } = props

  const base = [
    CLASSNAMES[size],
    'avatar',
    [
      'height-full width-full',
      'font-weight-medium',
      'background-cover background-center background-no-repeat',
      backgroundClass,
      colorClass,
      borderClass,
      className,
    ],
    {
      'radius-round': !square,
      'radius-small': square,
      'is-writing': isWriting,
    },
  ]

  if (url) {
    return classNames(base, [
      'height-full width-full',
      'background-cover background-center background-no-repeat',
    ])
  }
  return classNames(base, [
    'flexbox flexbox-centered',
    'capitalize font-weight-large',
  ])
}

const shorten = ({ name, letters }) =>
  name.replace(/[^a-zA-Z0-9\s:]*/g, '').slice(0, letters)

export default function Avatar(props) {
  const { contact, name, url, size, style = {} } = props
  const sizeStyle = STYLES[size]
  const className = getClassNamesForProps(props)

  let content = null
  let componentStyle = {
    ...sizeStyle,
    backgroundImage: `url(${url})`,
    ...style,
  }

  if (contact) {
    // Not sure about url validity, ImageLoader will test it
    return (
      <ImageLoader
        src={url}
        placeholder={Contact}
        style={{ ...sizeStyle, ...style }}
        className={className}
      />
    )
  }

  if (!url) {
    content = shorten({ name, letters: ['xs', 'xxs'].includes(size) ? 1 : 2 })
    componentStyle = { ...sizeStyle, lineHeight: `${SIZES[size]}px`, ...style }
  }

  return (
    <div className={className} style={componentStyle}>
      {content}
    </div>
  )
}

Avatar.propTypes = {
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(SIZES)),
  isWriting: PropTypes.bool,
}

Avatar.defaultProps = {
  size: 'base',
  isWriting: false,
}

export function AvatarWithOverlay(props) {
  const { style = {}, square, size } = props
  const sizeStyle = STYLES[size]

  // We reset positioning here because we don't it to be applied to the Avatar directly
  // when we are also using an overlay. The overlay container already has the positioning
  const avatarProps = {
    ...props,
    style,
  }

  return (
    <section
      className={classNames('avatar-overlay-container position-relative')}
      style={{ ...style, ...sizeStyle }}
    >
      <section
        style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
        className={classNames(
          ['avatar-overlay', 'position-absolute', 'width-full', 'height-full'],
          {
            'radius-round': !square,
            'radius-small': square,
          }
        )}
      >
        <section
          className={classNames(
            'avatar-writing-animation',
            `avatar-writing-animation--${size}`,
            'bold',
            'position-absolute height-full width-full flexbox flexbox-align-start flexbox-justify-center'
          )}
        >
          <span className="purple">.</span>
          <span className="purple">.</span>
          <span className="purple">.</span>
        </section>
      </section>
      <Avatar {...avatarProps} />
    </section>
  )
}

AvatarWithOverlay.propTypes = Avatar.propTypes
AvatarWithOverlay.defaultProps = Avatar.defaultProps

function UserAvatarLayout({
  userId,
  userLabel,
  userPhotoUrl,
  size,
  isWriting,
  positioning = {},
  tooltipContent,
  ...restProps
}) {
  const Component = isWriting ? AvatarWithOverlay : Avatar
  return (
    <TooltipOnHover
      id={`${userId}-presence`}
      tooltipContent={tooltipContent || userLabel}
      placement="left"
    >
      {/* A wrapping div seems mandatory here, don't ask why  */}
      <div style={{ ...positioning }}>
        <Component
          name={userLabel}
          url={userPhotoUrl}
          size={size}
          {...restProps}
        />
      </div>
    </TooltipOnHover>
  )
}

export const UserAvatar = compose(
  pure,
  connect((state, { userId }) => {
    const user = UsersSelectors.getUser(state, userId)
    return {
      userId,
      userLabel: user ? getLabel(user) : 'someone',
      userPhotoUrl:
        !user || user.invitePending ? DefaultUserAvatar : user.photoURL,
    }
  })
)(UserAvatarLayout)
